import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import "./TaskManager.css";

import {
  FaTasks,
  FaCheckCircle,
  FaSpinner,
  FaExclamationCircle,
  FaPlus,
  FaSearch,
  FaFilter,
  FaThLarge,
  FaList,
  FaCalendarAlt,
  FaTimes,
  FaSyncAlt,
} from "react-icons/fa";

import TaskBoardView from "./TaskBoardView";
import TaskListView from "./TaskListView";
import TaskCalendarView from "./TaskCalendarView";
import TaskSidebarPanel from "./TaskSidebarPanel";
import TaskModal from "./TaskModal";

const TaskManager = () => {
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    in_progress: 0,
    overdue: 0,
  });

  // Task list
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Current view: "board" | "list" | "calendar"
  const [currentView, setCurrentView] = useState("board");

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Current logged in user
  const [currentUser, setCurrentUser] = useState("User");

  const filterRef = useRef(null);

  // Load auth username
  useEffect(() => {
    const user = localStorage.getItem("username") || "User";
    setCurrentUser(user);
  }, []);

  // Close filter popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterOpen]);

  // Load stats & tasks
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // 1. Fetch Stats
      const statsRes = await axios.get(`${API_BASE_URL}/tasks/stats`, { headers });
      if (statsRes.data) {
        setStats({
          total: statsRes.data.total || 0,
          completed: statsRes.data.completed || 0,
          in_progress: statsRes.data.in_progress || 0,
          overdue: statsRes.data.overdue || 0,
        });
      }

      // 2. Fetch Tasks with search / filter params
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (overdueOnly) params.is_overdue = true;

      const tasksRes = await axios.get(`${API_BASE_URL}/tasks/`, { headers, params });
      setTasks(tasksRes.data || []);
    } catch (err) {
      console.error("Failed to load tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery, statusFilter, priorityFilter, overdueOnly]);

  // Handler: Create Task
  const handleOpenCreateModal = (presetData = {}) => {
    setSelectedTask({
      title: "",
      status: presetData.status || "To Do",
      priority: "Medium",
      due_date: presetData.due_date || new Date().toISOString(),
      ...presetData,
    });
    setModalOpen(true);
  };

  // Handler: Edit Task
  const handleEditTask = (task) => {
    setSelectedTask(task);
    setModalOpen(true);
  };

  // Handler: Duplicate Task
  const handleDuplicateTask = async (task) => {
    const duplicated = {
      ...task,
      title: `${task.title} (Copy)`,
      id: undefined,
      created_at: undefined,
      updated_at: undefined,
      completed_at: undefined,
    };
    await handleSaveTask(duplicated);
  };

  // Handler: Delete Task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_BASE_URL}/tasks/${taskId}`, { headers });
      fetchData();
    } catch (err) {
      console.error("Failed to delete task:", err);
      alert("Error deleting task.");
    }
  };

  // Handler: Quick Status Change
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(
        `${API_BASE_URL}/tasks/${taskId}?current_user=${encodeURIComponent(currentUser)}`,
        { status: newStatus },
        { headers }
      );
      fetchData();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  // Handler: Save Task from Modal (Create or Update)
  const handleSaveTask = async (formData, taskId) => {
    const token = localStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    let savedTask = null;
    if (taskId) {
      // Update
      const res = await axios.put(
        `${API_BASE_URL}/tasks/${taskId}?current_user=${encodeURIComponent(currentUser)}`,
        formData,
        { headers }
      );
      savedTask = res.data;
    } else {
      // Create
      const res = await axios.post(
        `${API_BASE_URL}/tasks/?current_user=${encodeURIComponent(currentUser)}`,
        formData,
        { headers }
      );
      savedTask = res.data;
    }
    fetchData();
    return savedTask;
  };

  const activeFilterCount =
    (statusFilter ? 1 : 0) + (priorityFilter ? 1 : 0) + (overdueOnly ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter("");
    setPriorityFilter("");
    setOverdueOnly(false);
  };

  return (
    <div className="tm-container">
      {/* ===================================================
          A. TOP SECTION — OVERVIEW CARDS
          =================================================== */}
      <div className="tm-overview-grid">
        {/* Total Tasks (Blue) */}
        <div className="tm-kpi-card tm-kpi-blue">
          <div className="tm-kpi-info">
            <h6>Total Tasks</h6>
            <h3>{stats.total}</h3>
          </div>
          <div className="tm-kpi-icon">
            <FaTasks />
          </div>
        </div>

        {/* Completed (Green) */}
        <div className="tm-kpi-card tm-kpi-green">
          <div className="tm-kpi-info">
            <h6>Completed</h6>
            <h3>{stats.completed}</h3>
          </div>
          <div className="tm-kpi-icon">
            <FaCheckCircle />
          </div>
        </div>

        {/* In Progress (Purple) */}
        <div className="tm-kpi-card tm-kpi-purple">
          <div className="tm-kpi-info">
            <h6>In Progress</h6>
            <h3>{stats.in_progress}</h3>
          </div>
          <div className="tm-kpi-icon">
            <FaSpinner />
          </div>
        </div>

        {/* Overdue (Red) */}
        <div className="tm-kpi-card tm-kpi-red">
          <div className="tm-kpi-info">
            <h6>Overdue</h6>
            <h3>{stats.overdue}</h3>
          </div>
          <div className="tm-kpi-icon">
            <FaExclamationCircle />
          </div>
        </div>
      </div>

      {/* ===================================================
          B. MIDDLE SECTION — HEADER & CONTROLS
          =================================================== */}
      <div className="tm-main-header">
        {/* Heading */}
        <div className="tm-heading-area">
          <h2>Task Management</h2>
          <p>Manage, prioritize, and track your team's work.</p>
        </div>

        {/* Controls on the Right */}
        <div className="tm-controls-area">
          {/* + Create Task button */}
          <button
            className="tm-btn tm-btn-primary"
            onClick={() => handleOpenCreateModal()}
          >
            <FaPlus /> Create Task
          </button>

          {/* Search field */}
          <div className="tm-search-box">
            <FaSearch className="tm-search-icon" />
            <input
              type="text"
              placeholder="Search tasks, clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: "pointer",
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Filter button with Popover */}
          <div style={{ position: "relative" }} ref={filterRef}>
            <button
              className={`tm-btn tm-btn-secondary ${activeFilterCount > 0 ? "active" : ""}`}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <FaFilter />
              Filter {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </button>

            {filterOpen && (
              <div className="tm-filter-popover">
                <h5>
                  <span>Smart Filters</span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#0284c7",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Reset all
                    </button>
                  )}
                </h5>

                <div className="tm-filter-group">
                  <label>Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="In Review">In Review</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>

                <div className="tm-filter-group">
                  <label>Priority</label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                  >
                    <option value="">All Priorities</option>
                    <option value="Urgent">Urgent</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                <div className="tm-filter-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={overdueOnly}
                      onChange={(e) => setOverdueOnly(e.target.checked)}
                      style={{ width: "auto" }}
                    />
                    <span>Show Overdue Only</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Refresh */}
          <button
            className="tm-btn tm-btn-secondary"
            title="Refresh Tasks"
            onClick={fetchData}
          >
            <FaSyncAlt className={loading ? "fa-spin" : ""} />
          </button>

          {/* View selector: Board | List | Calendar */}
          <div className="tm-view-selector">
            <button
              className={`tm-view-btn ${currentView === "board" ? "active" : ""}`}
              onClick={() => setCurrentView("board")}
              title="Board View"
            >
              <FaThLarge /> Board
            </button>
            <button
              className={`tm-view-btn ${currentView === "list" ? "active" : ""}`}
              onClick={() => setCurrentView("list")}
              title="List View"
            >
              <FaList /> List
            </button>
            <button
              className={`tm-view-btn ${currentView === "calendar" ? "active" : ""}`}
              onClick={() => setCurrentView("calendar")}
              title="Calendar View"
            >
              <FaCalendarAlt /> Calendar
            </button>
          </div>
        </div>
      </div>

      {/* ===================================================
          MAIN WORKSPACE LAYOUT (VIEWS + RIGHT SIDEBAR)
          =================================================== */}
      <div className="tm-layout-wrapper">
        {/* Main Content View */}
        <div className="tm-content-area">
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
              <FaSpinner className="fa-spin" style={{ fontSize: "28px", marginBottom: "12px" }} />
              <p>Loading tasks from CRM...</p>
            </div>
          ) : currentView === "board" ? (
            <TaskBoardView
              tasks={tasks}
              onEdit={handleEditTask}
              onDuplicate={handleDuplicateTask}
              onDelete={handleDeleteTask}
              onStatusChange={handleStatusChange}
              onCreateWithStatus={(status) => handleOpenCreateModal({ status })}
            />
          ) : currentView === "list" ? (
            <TaskListView
              tasks={tasks}
              onEdit={handleEditTask}
              onDuplicate={handleDuplicateTask}
              onDelete={handleDeleteTask}
              onStatusChange={handleStatusChange}
            />
          ) : (
            <TaskCalendarView
              tasks={tasks}
              onEdit={handleEditTask}
              onCreateWithDate={(dateStr) =>
                handleOpenCreateModal({ due_date: dateStr, start_date: dateStr })
              }
            />
          )}
        </div>

        {/* D. Right-Side Panel */}
        <TaskSidebarPanel
          tasks={tasks}
          onEdit={handleEditTask}
          currentUsername={currentUser}
        />
      </div>

      {/* Create / Edit Modal Dialog */}
      <TaskModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        task={selectedTask}
        onSave={handleSaveTask}
      />
    </div>
  );
};

export default TaskManager;
